/**
 * WPP UI — Alpine registration.
 *
 * Call once, after Alpine exists and before Livewire starts:
 *
 *     import { Alpine, Livewire } from '../../vendor/livewire/livewire/dist/livewire.esm';
 *     import { registerWppUi } from '../../vendor/bernskiold/wpp-ui-blade/resources/js';
 *
 *     registerWppUi(Alpine);
 *     Livewire.start();
 *
 * The WYSIWYG field is registered separately, from `./wysiwyg.js`, because it is
 * the one component that pulls a heavy dependency (TipTap) and most
 * applications never render it.
 */
import Tooltip from '@ryangjchandler/alpine-tooltip';
import tippy from 'tippy.js';

import accordion from './components/accordion';
import banner from './components/banner';
import bulkSelection from './components/bulk-selection';
import collapsibleNav from './components/collapsible-nav';
import contextMenu from './components/context-menu';
import disclosure from './components/disclosure';
import dropdown from './components/dropdown';
import fileUpload from './components/file-upload';
import multiselect from './components/multiselect';
import notifications from './components/notifications';
import pagination from './components/pagination';
import rangeInput from './components/range-input';
import searchableSelect from './components/searchable-select';
import segmentControl from './components/segment-control';
import tabs from './components/tabs';
import toggle from './components/toggle';
import translatableFormField from './components/translatable-form-field';
import tree from './components/tree';
import treeItem from './components/tree-item';
import wppTable from './components/wpp-table';

export { datavizPalette, datavizRamp, datavizColor, datavizPalettes, datavizRamps } from './dataviz';

/**
 * @param {import('alpinejs').Alpine} Alpine
 * @param {{ tooltip?: boolean }} [options]  Pass `tooltip: false` if the
 *        application already registers the Alpine tooltip plugin itself;
 *        registering it twice is harmless but noisy.
 */
export function registerWppUi(Alpine, options = {}) {
    const { tooltip = true } = options;

    if (tooltip) {
        Alpine.plugin(Tooltip);

        /*
         * ODS puts a tooltip 4px from the element it describes; Tippy's own
         * default is 10px. Set once here rather than as a modifier at every call
         * site — `tippy.js` is a dependency of the Alpine plugin and resolves to
         * the same module, so this reaches every tooltip the plugin creates.
         *
         * The appearance is not set here. It comes from `tooltip.css`, which
         * styles `.tippy-box` directly rather than registering a named theme, so
         * a tooltip looks right without any call site opting in — including the
         * ones already written in The Fount.
         */
        tippy.setDefaultProps({ offset: [0, 4] });
    }

    Alpine.data('accordion', accordion);
    Alpine.data('banner', banner);
    Alpine.data('bulkSelection', bulkSelection);
    Alpine.data('collapsibleNav', collapsibleNav);
    Alpine.data('contextMenu', contextMenu);
    Alpine.data('disclosure', disclosure);
    Alpine.data('dropdown', dropdown);
    Alpine.data('fileUpload', fileUpload);
    Alpine.data('multiselect', multiselect);
    Alpine.data('wppNotifications', notifications);
    Alpine.data('pagination', pagination);
    Alpine.data('rangeInput', rangeInput);
    Alpine.data('searchableSelect', searchableSelect);
    Alpine.data('segmentControl', segmentControl);
    Alpine.data('tabs', tabs);
    Alpine.data('toggle', toggle);
    Alpine.data('translatableFormField', translatableFormField);
    Alpine.data('tree', tree);
    Alpine.data('treeItem', treeItem);
    Alpine.data('wppTable', wppTable);
}

export {
    accordion,
    banner,
    bulkSelection,
    collapsibleNav,
    contextMenu,
    disclosure,
    dropdown,
    fileUpload,
    multiselect,
    pagination,
    searchableSelect,
    segmentControl,
    tabs,
    toggle,
    translatableFormField,
    tree,
    treeItem,
    wppTable,
};
